import { D, F, G, S } from "@mobily/ts-belt";
import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import useSWR from "swr";
import { SignType } from "../../domain";
import { fetcher } from "../../hooks";

export const MAX_PRICE = 20_000;

export const allSignedTypes = SignType.list().reduce((acc, type) => {
  acc[type] = true;
  return acc;
}, {});

export const defaultFilter = {
  order: "ra",
  type: {},
  maxPrice: 0,
  minPrice: 0,
  franchise: {},
  signedType: {},
  cursor: "",
  limit: 24,
  name: "",
};

const keys = new Set(Object.keys(defaultFilter));

const parseValue = (key, value) => {
  if (key !== "category") return value;

  return Object.entries(value).flatMap(([categoryId, subcategories]) => {
    if (!G.isObject(subcategories)) return [categoryId];

    const subIds = Object.entries(subcategories)
      .filter(([, has]) => has)
      .map(([subcategoryId]) => `${categoryId}.${subcategoryId}`);

    return [categoryId, ...subIds];
  }, []);
};

export const removeEmptyFilterKeys = (filter) =>
  Object.entries(filter).reduce((result, [key, value]) => {
    if (keys.has(key) && !F.equals(value, defaultFilter[key])) {
      result[key] = parseValue(key, value);
    }
    return result;
  }, {});

const defaultFilters = {
  sort: [
    { id: "ra", label: "Most Popular" },
    { id: "cd", label: "Newest" },
    { id: "pa", label: "Price: Low to High" },
    { id: "pd", label: "Price: High to Low" },
  ],
  franchises: [],
  signedTypes: [],
  productTypes: [],
  priceRange: { minCents: 0, maxCents: 0 },
};

const defaultPagination = {
  totalResults: 0,
  currentResults: 0,
  pageSize: 24,
  hasNext: false,
  nextCursor: null,
};

function isValidOrder(order) {
  return (
    G.isString(order) &&
    S.isNotEmpty(order) &&
    defaultFilters.sort.some((filter) => filter.id === order)
  );
}

function isValidPrice(price) {
  return (
    G.isNumber(price) &&
    !Number.isNaN(price) &&
    price >= 0 &&
    price <= MAX_PRICE
  );
}

function isValidNumber(num) {
  return G.isNumber(num) && !Number.isNaN(num) && num >= 0;
}

function isValidSignedType(signedTypes) {
  return (
    G.isArray(signedTypes) && signedTypes.every((type) => allSignedTypes[type])
  );
}

const keyMap = {
  order: "o",
  type: "ty",
  maxPrice: "mp",
  minPrice: "mi",
  franchise: "f",
  signedType: "st",
  cursor: "c",
  limit: "l",
  name: "q",
};

function toMap(acc, value) {
  acc[value] = true;
  return acc;
}

const paramMap = {
  o: {
    name: "order",
    parser: (value) => (isValidOrder(value) ? value : "ra"),
  },
  ty: {
    name: "type",
    parser: (value) => value.split(",").reduce(toMap, {}),
  },
  mp: {
    name: "maxPrice",
    parser: (value) => (isValidPrice(Number(value)) ? Number(value) : 0),
  },
  mi: {
    name: "minPrice",
    parser: (value) => (isValidPrice(Number(value)) ? Number(value) : 0),
  },
  f: {
    name: "franchise",
    parser: (value) => value.split(",").reduce(toMap, {}),
  },
  st: {
    name: "signedType",
    parser: (value) => {
      const values = value.split(",");

      if (isValidSignedType(values)) {
        return values.reduce((acc, type) => {
          acc[type] = true;
          return acc;
        }, {});
      }

      return {};
    },
  },
  c: {
    name: "cursor",
    parser: (value) => value,
  },
  l: {
    name: "limit",
    parser: (value) => (isValidNumber(Number(value)) ? Number(value) : 24),
  },
  q: {
    name: "name",
    parser: (value) => value,
  },
};

function parseDefaultsFromParams(search) {
  const params = new URLSearchParams(search);

  if (params.size === 0) {
    return defaultFilter;
  }

  const result = {};

  for (const [key, value] of params.entries()) {
    const mappedKey = paramMap[key];

    if (!mappedKey) {
      continue;
    }

    const { name, parser } = mappedKey;

    const parsedValue = parser(value);

    result[name] = parsedValue;
  }

  return D.merge(defaultFilter, result);
}

export function useFilterParams() {
  const location = useLocation();
  const history = useHistory();

  const [filter, setFilter] = React.useState(() =>
    parseDefaultsFromParams(location.search),
  );

  const updateFilterAndSetParams = React.useCallback(
    (updateFnOrFilter) => {
      let updateFn = updateFnOrFilter;

      if (typeof updateFn !== "function") {
        updateFn = () => updateFnOrFilter;
      }

      setFilter((filter) => {
        const newFilter = updateFn(filter);

        const search = Object.entries(newFilter)
          .reduce((result, [key, value]) => {
            const mappedKey = keyMap[key];

            if (G.isString(value)) {
              result.set(mappedKey, value);
            } else if (G.isNumber(value)) {
              result.set(mappedKey, String(value));
            } else if (G.isObject(value)) {
              const values = Object.keys(value).filter((key) => value[key]);

              if (values.length > 0) {
                result.set(mappedKey, values.join(","));
              }
            } else if (G.isArray(value)) {
              if (value.length > 0) {
                result.set(mappedKey, value.join(","));
              }
            }

            return result;
          }, new URLSearchParams())
          .toString();

        history.replace({ search });

        return newFilter;
      });
    },
    [history],
  );

  return { filter, setFilter: updateFilterAndSetParams };
}

function hasMaxPrice(maxPrice) {
  return maxPrice > 0;
}

function hasMinPrice({ minPrice, maxPrice }) {
  return minPrice > 0 && (minPrice < maxPrice || !hasMaxPrice(maxPrice));
}

function hasName(name) {
  return G.isString(name) && S.isNotEmpty(name);
}

function hasCursor(cursor) {
  return G.isString(cursor) && S.isNotEmpty(cursor);
}

function paramsFor({
  franchise,
  maxPrice,
  minPrice,
  name,
  order,
  type,
  signedType,
  cursor,
}) {
  const params = new URLSearchParams({
    order,
  });

  if (hasName(name)) {
    params.set("name", name);
  }

  if (hasMaxPrice(maxPrice)) {
    params.set("max_price", maxPrice);
  }

  if (hasMinPrice({ minPrice, maxPrice })) {
    params.set("min_price", minPrice);
  }

  if (type) {
    const typeArr = Object.keys(type).filter((key) => type[key]);
    if (typeArr.length > 0) {
      params.set("type", typeArr.join(","));
    }
  }

  if (franchise) {
    const franchiseArr = Object.keys(franchise).filter((key) => franchise[key]);
    if (franchiseArr.length > 0) {
      params.set("franchise", franchiseArr.join(","));
    }
  }

  if (signedType) {
    const signedTypeArr = Object.keys(signedType).filter(
      (key) => signedType[key],
    );
    if (signedTypeArr.length > 0) {
      params.set("signed_type", signedTypeArr.join(","));
    }
  }

  if (hasCursor(cursor)) {
    params.set("cursor", cursor);
  }

  return params;
}

export function useProducts(filter = defaultFilter) {
  const { data, error, isValidating, mutate } = useSWR(
    `${
      import.meta.env.VITE_DISCOVER_API_ENDPOINT
    }/v1/browse/products?${paramsFor(filter)}`,
    fetcher,
    { keepPreviousData: true },
  );

  const products = React.useMemo(() => {
    return data?.products ?? [];
  }, [data]);

  const pagination = React.useMemo(() => {
    return data?.pagination || defaultPagination;
  }, [data]);

  return {
    error,
    isError: Boolean(error),
    isLoading: !data && !error,
    isValidating,
    mutate,
    products,
    pagination,
  };
}

const BROWSE_PRODUCTS_FILTERS_URL = `${
  import.meta.env.VITE_S3_CLOUDFRONT_URL
}/filters/browse-products-filters.json`;

export function useFilters() {
  const { data, error, isValidating, mutate } = useSWR(
    BROWSE_PRODUCTS_FILTERS_URL,
    fetcher,
    {
      keepPreviousData: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      dedupingInterval: 900_000,
    },
  );

  const filters = React.useMemo(() => {
    return data || defaultFilters;
  }, [data]);

  return {
    error,
    filters,
    isError: Boolean(error),
    isLoading: !data && !error,
    isValidating,
    mutate,
  };
}
